import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

interface AuthorizationDetails {
    client: {
        name: string;
        logo_uri?: string;
        client_uri?: string;
    };
    scopes: string[];
    redirect_uri: string;
}

// Scope icons mapping
const scopeIcons: Record<string, string> = {
    budget: '📖',
    account: '🏦',
    transaction: '💸'
};

const OAuthConsentPage = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const authorizationId = searchParams.get('authorization_id');
    const [details, setDetails] = useState<AuthorizationDetails | null>(null);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const { session, loading: authLoading } = useAuth();
    
    // Ref to prevent double-fetching in React StrictMode
    const fetchingRef = useRef(false);
    const fetchedRef = useRef(false);

    useEffect(() => {
        // Wait for auth to load
        if (authLoading) return;

        // TEST MODE: For UI development only
        /* if (authorizationId === 'test-mode') {
            setDetails({
                client: {
                    name: 'Test MCP Client',
                    logo_uri: undefined,
                    client_uri: 'https://example.com'
                },
                scopes: ['budget', 'account', 'transaction'],
                redirect_uri: 'https://example.com/callback'
            });
            setLoading(false);
            return;
        } */

        // If not logged in, redirect to login with return URL
        if (!session) {
            const returnUrl = `/oauth/consent?authorization_id=${authorizationId}`;
            console.log('[OAuthConsent] No session, redirecting to login with return URL:', returnUrl);
            navigate(`/login?redirect=${encodeURIComponent(returnUrl)}`);
            return;
        }

        console.log('[OAuthConsent] Session found, fetching authorization details for:', authorizationId);

        // Prevent double-fetching in React StrictMode
        if (fetchingRef.current || fetchedRef.current) {
            console.log('[OAuthConsent] Already fetching or fetched, skipping...');
            return;
        }
        
        // Set the ref IMMEDIATELY before starting the async function
        // This prevents race conditions in StrictMode
        fetchingRef.current = true;

        // Fetch authorization details
        async function fetchDetails() {
            console.log('[OAuthConsent] Version: GrantFix-v10 (Pre-Revoke)');
            
            if (!authorizationId) {
                console.error('[OAuthConsent] Missing authorization_id parameter');
                setError('Missing authorization_id parameter');
                setLoading(false);
                fetchingRef.current = false;
                return;
            }

            try {
                const authAny = supabase.auth as any;
                
                // STEP 1: Check for existing grants FIRST
                // If grant exists, Supabase will auto-approve on getAuthorizationDetails
                // but won't return redirect_to - causing the flow to break
                // SOLUTION: Revoke grant BEFORE getAuthorizationDetails to prevent auto-approval
                
                if (authAny.oauth && typeof authAny.oauth.listGrants === 'function') {
                    console.log('[OAuthConsent] Checking for existing grants...');
                    const { data: grants } = await authAny.oauth.listGrants();
                    if (grants && grants.length > 0) {
                        console.log('[OAuthConsent] Found existing grants:', grants.length, '- Revoking BEFORE getAuthorizationDetails to prevent auto-approval bug');
                        
                        // Revoke grants BEFORE calling getAuthorizationDetails
                        // This prevents Supabase from auto-approving without returning redirect_to
                        try {
                            const backendUrl = import.meta.env.VITE_BACKEND_URL || 'https://api.fittory.id';
                            const revokeResponse = await fetch(`${backendUrl}/auth/revoke-oauth-grants?force_cleanup=true`, {
                                method: 'DELETE',
                                headers: {
                                    'Authorization': `Bearer ${session?.access_token}`,
                                    'Content-Type': 'application/json'
                                }
                            });
                            
                            if (revokeResponse.ok) {
                                console.log('[OAuthConsent] Pre-emptive grant revocation successful. Continuing with fresh consent...');
                            } else {
                                console.warn('[OAuthConsent] Pre-emptive grant revocation failed, continuing anyway...');
                            }
                        } catch (e) {
                            console.warn('[OAuthConsent] Pre-emptive grant revocation exception:', e);
                        }
                        
                        // Small delay to ensure revocation propagates
                        await new Promise(resolve => setTimeout(resolve, 500));
                    }
                }
                
                console.log('[OAuthConsent] Calling getAuthorizationDetails...');
                // Use SDK oauth methods (available in v2.86+)
                const { data, error: oauthError } = await authAny.oauth.getAuthorizationDetails(authorizationId);
                
                // If already processed by another call, skip
                if (fetchedRef.current) {
                    console.log('[OAuthConsent] Already processed, skipping');
                    return;
                }

                console.log('[OAuthConsent] getAuthorizationDetails response:', { data, oauthError });

                // Handle auto_approved case - Supabase already approved, redirect immediately
                if (data?.auto_approved) {
                    console.log('[OAuthConsent] Auto-approved detected! Full data:', JSON.stringify(data, null, 2));
                    fetchedRef.current = true;
                    
                    // Check multiple possible locations for redirect_to
                    const redirectTo = data?.redirect_to || data?.redirectTo || data?.callback_url || data?.callbackUrl;
                    
                    if (redirectTo) {
                        console.log('[OAuthConsent] Redirecting to:', redirectTo);
                        window.location.href = redirectTo;
                        return;
                    }
                    
                    // If we have authorization code and redirect_uri, construct the redirect URL
                    const code = data?.code || data?.authorization_code;
                    const redirectUri = data?.redirect_uri || data?.redirectUri;
                    const state = data?.state;
                    
                    if (code && redirectUri) {
                        const redirectUrl = new URL(redirectUri);
                        redirectUrl.searchParams.set('code', code);
                        if (state) redirectUrl.searchParams.set('state', state);
                        console.log('[OAuthConsent] Constructed redirect URL:', redirectUrl.toString());
                        window.location.href = redirectUrl.toString();
                        return;
                    }
                    
                    // Auto-approved but no redirect URL - stale consent issue
                    // Revoke consents and ask user to retry from ChatGPT
                    console.log('[OAuthConsent] Auto-approved but no redirect URL. Revoking stale consents...');
                    
                    try {
                        const backendUrl = import.meta.env.VITE_BACKEND_URL || 'https://api.fittory.id';
                        const response = await fetch(`${backendUrl}/auth/revoke-oauth-grants?force_cleanup=true`, {
                            method: 'DELETE',
                            headers: {
                                'Authorization': `Bearer ${session?.access_token}`,
                                'Content-Type': 'application/json'
                            }
                        });
                        
                        if (response.ok) {
                            console.log('[OAuthConsent] Stale consents revoked. Please retry from ChatGPT.');
                            setError(t('oauth_consent.stale_consent_error'));
                        } else {
                            setError(t('oauth_consent.already_approved_error'));
                        }
                    } catch (e) {
                        console.warn('[OAuthConsent] Failed to revoke stale consents:', e);
                        setError(t('oauth_consent.already_approved_error'));
                    }
                    
                    setLoading(false);
                    return;
                }

                if (oauthError) {
                    console.error('[OAuthConsent] OAuth error:', oauthError);

                    // If authorization request cannot be processed, try to clean up old grants
                    if (oauthError.message?.includes('cannot be processed') || 
                        oauthError.message?.includes('conflict') ||
                        oauthError.message?.includes('already exists')) {
                        console.log('[OAuthConsent] Authorization conflict detected. Attempting cleanup...');

                        try {
                            const backendUrl = import.meta.env.VITE_BACKEND_URL || 'https://api.fittory.id';
                            
                            // Strategy 1: Try to get existing grants and revoke them specifically
                            const authAny = supabase.auth as any;
                            let cleanupAttempted = false;
                            
                            if (authAny.oauth && typeof authAny.oauth.listGrants === 'function') {
                                console.log('[OAuthConsent] Fetching existing grants...');
                                const { data: grants, error: grantsError } = await authAny.oauth.listGrants();

                                if (!grantsError && grants && grants.length > 0) {
                                    console.log(`[OAuthConsent] Found ${grants.length} grants. Cleaning up...`, grants);

                                    for (const grant of grants) {
                                        const clientId =
                                            grant.clientId ||
                                            grant.client_id ||
                                            grant.client?.id ||
                                            grant.client?.client_id;

                                        if (!clientId) continue;

                                        try {
                                            console.log(`[OAuthConsent] Requesting cleanup for client: ${clientId}`);
                                            const response = await fetch(`${backendUrl}/auth/revoke-oauth-grants?client_id=${clientId}`, {
                                                method: 'DELETE',
                                                headers: {
                                                    'Authorization': `Bearer ${session?.access_token}`,
                                                    'Content-Type': 'application/json'
                                                }
                                            });

                                            if (response.ok) {
                                                console.log(`[OAuthConsent] Cleanup successful for ${clientId}`);
                                                cleanupAttempted = true;
                                            } else {
                                                const err = await response.json();
                                                console.warn('[OAuthConsent] Cleanup warning:', err);
                                            }
                                        } catch (e) {
                                            console.warn('[OAuthConsent] Cleanup exception:', e);
                                        }
                                    }
                                }
                            }

                            // Strategy 2: Force comprehensive cleanup if specific cleanup didn't work
                            if (!cleanupAttempted) {
                                console.log('[OAuthConsent] Attempting force cleanup...');
                                try {
                                    const response = await fetch(`${backendUrl}/auth/revoke-oauth-grants?force_cleanup=true`, {
                                        method: 'DELETE',
                                        headers: {
                                            'Authorization': `Bearer ${session?.access_token}`,
                                            'Content-Type': 'application/json'
                                        }
                                    });

                                    if (response.ok) {
                                        console.log('[OAuthConsent] Force cleanup successful');
                                        cleanupAttempted = true;
                                    }
                                } catch (e) {
                                    console.warn('[OAuthConsent] Force cleanup exception:', e);
                                }
                            }

                            // Wait a moment for Supabase to process the cleanup
                            if (cleanupAttempted) {
                                console.log('[OAuthConsent] Waiting for cleanup to propagate...');
                                await new Promise(resolve => setTimeout(resolve, 1000));
                            }

                            // Retry authorization
                            console.log('[OAuthConsent] Retrying authorization...');
                            const { data: retryData, error: retryError } = await (supabase.auth as any).oauth.getAuthorizationDetails(authorizationId);

                            if (!retryError && retryData) {
                                console.log('[OAuthConsent] Retry successful!');
                                setDetails(retryData);
                                setLoading(false);
                                return;
                            } else {
                                console.error('[OAuthConsent] Retry failed:', retryError);
                            }

                        } catch (err) {
                            console.error('[OAuthConsent] Cleanup process failed:', err);
                        }

                        // Provide helpful error message with instructions
                        setError(t('oauth_consent.conflict_error'));
                    } else {
                        setError(oauthError.message || 'Failed to get authorization details');
                    }

                    setLoading(false);
                    return;
                }

                if (!data) {
                    console.error('[OAuthConsent] No data returned from getAuthorizationDetails');
                    setError('Failed to get authorization details - no data returned');
                    setLoading(false);
                    return;
                }

                fetchedRef.current = true;
                setDetails(data);
                setLoading(false);
            } catch (err) {
                console.error('[OAuthConsent] Exception in fetchDetails:', err);
                setError(err instanceof Error ? err.message : 'Failed to load authorization details');
                setLoading(false);
            } finally {
                fetchingRef.current = false;
            }
        }

        fetchDetails();
    }, [authorizationId, session, authLoading, navigate]);

    const handleApprove = async () => {
        if (!authorizationId) return;

        setProcessing(true);
        setError('');

        try {
            const { data, error: oauthError } = await (supabase.auth as any).oauth.approveAuthorization(authorizationId);

            if (oauthError) {
                setError(oauthError.message || 'Failed to approve authorization');
                setProcessing(false);
                return;
            }

            // Redirect to the callback URL
            if (data?.redirect_to) {
                window.location.href = data.redirect_to;
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to approve authorization');
            setProcessing(false);
        }
    };

    const handleDeny = async () => {
        if (!authorizationId) return;

        setProcessing(true);
        setError('');

        try {
            const { data, error: oauthError } = await (supabase.auth as any).oauth.denyAuthorization(authorizationId);

            if (oauthError) {
                setError(oauthError.message || 'Failed to deny authorization');
                setProcessing(false);
                return;
            }

            // Redirect to the callback URL (with error)
            if (data?.redirect_to) {
                window.location.href = data.redirect_to;
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to deny authorization');
            setProcessing(false);
        }
    };

    // Loading state
    if (loading || authLoading) {
        return (
            <div
                className="min-h-screen bg-[#ffffeb] flex items-center justify-center px-4 py-2"
                style={{
                    backgroundImage: `
            repeating-linear-gradient(0deg, transparent, transparent 7px, rgba(7, 127, 93, 0.03) 7px, rgba(7, 127, 93, 0.03) 8px),
            repeating-linear-gradient(90deg, transparent, transparent 7px, rgba(7, 127, 93, 0.03) 7px, rgba(7, 127, 93, 0.03) 8px)
          `
                }}
            >
                <div className="w-full max-w-md">
                    <div
                        className="bg-white border-4 border-[#077f5d] p-8 text-center"
                        style={{ boxShadow: '6px 6px 0 #077f5d' }}
                    >
                        <div className="animate-pulse">
                            <div className="h-16 w-16 mx-auto mb-4 bg-[#3cbe8c] border-4 border-[#077f5d]" style={{ boxShadow: '4px 4px 0 #077f5d' }}></div>
                            <div className="h-4 bg-[#a9edba] mb-2 w-3/4 mx-auto"></div>
                            <div className="h-4 bg-[#a9edba] w-1/2 mx-auto"></div>
                        </div>
                        <p
                            className="mt-6 text-[#077f5d] uppercase"
                            style={{ fontFamily: "'Press Start 2P', monospace", fontSize: '10px' }}
                        >
                            {t('oauth_consent.loading')}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // Error state (no authorization_id)
    if (!authorizationId || !details) {
        return (
            <div
                className="min-h-screen bg-[#ffffeb] flex items-center justify-center px-4 py-2"
                style={{
                    backgroundImage: `
            repeating-linear-gradient(0deg, transparent, transparent 7px, rgba(7, 127, 93, 0.03) 7px, rgba(7, 127, 93, 0.03) 8px),
            repeating-linear-gradient(90deg, transparent, transparent 7px, rgba(7, 127, 93, 0.03) 7px, rgba(7, 127, 93, 0.03) 8px)
          `
                }}
            >
                <div className="w-full max-w-md">
                    <div
                        className="bg-white border-4 border-[#077f5d] p-8"
                        style={{ boxShadow: '6px 6px 0 #077f5d' }}
                    >
                        <div className="flex items-center justify-center mb-6">
                            <div
                                className="bg-red-500 border-4 border-[#077f5d] p-4"
                                style={{ boxShadow: '4px 4px 0 #077f5d' }}
                            >
                                <span className="text-4xl">❌</span>
                            </div>
                        </div>
                        <h1
                            className="text-center text-[#077f5d] uppercase mb-4"
                            style={{ fontFamily: "'Press Start 2P', monospace", fontSize: '12px' }}
                        >
                            {t('oauth_consent.error')}
                        </h1>
                        <p className="text-center font-mono text-[#1a1a1a] text-sm">
                            {error || t('oauth_consent.invalid_request')}
                        </p>
                        <button
                            onClick={() => navigate('/')}
                            className="w-full mt-6 bg-[#3cbe8c] text-white px-8 py-4 border-2 border-[#077f5d] font-bold uppercase"
                            style={{
                                fontFamily: "'Press Start 2P', monospace",
                                fontSize: '10px',
                                boxShadow: '6px 6px 0 #077f5d'
                            }}
                        >
                            {t('oauth_consent.back_to_home')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className="min-h-screen bg-[#ffffeb] flex items-center justify-center px-4 py-8"
            style={{
                backgroundImage: `
          repeating-linear-gradient(0deg, transparent, transparent 7px, rgba(7, 127, 93, 0.03) 7px, rgba(7, 127, 93, 0.03) 8px),
          repeating-linear-gradient(90deg, transparent, transparent 7px, rgba(7, 127, 93, 0.03) 7px, rgba(7, 127, 93, 0.03) 8px)
        `
            }}
        >
            <div className="w-full max-w-md">
                <div
                    className="bg-white border-4 border-[#077f5d] p-8"
                    style={{ boxShadow: '6px 6px 0 #077f5d' }}
                >
                    {/* Logo Header */}
                    <div className="flex items-center justify-center mb-6">
                        <div
                            className="bg-[#3cbe8c] border-4 border-[#077f5d] p-1"
                            style={{ boxShadow: '6px 6px 0 #077f5d' }}
                        >
                            <picture>
                                <source srcSet="/Fittory.avif" type="image/avif" />
                                <source srcSet="/Fittory.webp" type="image/webp" />
                                <img
                                    src="/Fittory.png"
                                    alt="Fittory"
                                    className="h-12 w-auto"
                                />
                            </picture>
                        </div>
                    </div>

                    {/* Client Info */}
                    <div
                        className="text-center bg-[#a9edba] border-2 border-[#077f5d] p-4 mb-6"
                        style={{ boxShadow: '4px 4px 0 #077f5d' }}
                    >
                        <p className="font-bold text-[#077f5d] font-mono">{details.client?.name || t('oauth_consent.external_app')}</p>
                        <p className="text-xs text-[#1a1a1a] font-mono">{t('oauth_consent.wants_access')}</p>
                    </div>

                    {error && (
                        <div
                            className="bg-red-500 text-white border-2 border-[#077f5d] px-4 py-3 mb-4 font-mono text-sm"
                            style={{ boxShadow: '4px 4px 0 #077f5d' }}
                        >
                            {error}
                        </div>
                    )}

                    {/* Permissions */}
                    <div className="mb-6">
                        <p
                            className="text-[#077f5d] uppercase mb-3"
                            style={{ fontFamily: "'Press Start 2P', monospace", fontSize: '8px' }}
                        >
                            {t('oauth_consent.permissions_requested')}
                        </p>
                        <div className="space-y-2">
                            {/* Always show Fittory scopes, ignore OIDC scopes from Supabase */}
                            {['budget', 'account', 'transaction'].map((scope) => {
                                const scopeName = t(`oauth_consent.scopes.${scope}.name`);
                                const scopeDescription = t(`oauth_consent.scopes.${scope}.description`);
                                const scopeIcon = scopeIcons[scope] || '🔐';
                                return (
                                    <div
                                        key={scope}
                                        className="bg-[#ffffeb] border-2 border-[#077f5d] p-3 flex items-center gap-3"
                                    >
                                        <span className="text-xl">{scopeIcon}</span>
                                        <div>
                                            <p className="font-bold text-[#1a1a1a] font-mono text-sm">{scopeName}</p>
                                            <p className="text-xs text-gray-600 font-mono">{scopeDescription}</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* User Info */}
                    <div className="border-t-2 border-[#077f5d] pt-4 mb-6">
                        <p className="text-xs text-gray-500 font-mono text-center">
                            {t('oauth_consent.logged_in_as')} <span className="font-bold text-[#077f5d]">{session?.user?.email}</span>
                        </p>
                        <p className="text-xs text-center mt-2">
                            <button
                                onClick={async () => {
                                    const returnUrl = `/oauth/consent?authorization_id=${authorizationId}`;
                                    await supabase.auth.signOut();
                                    navigate(`/login?redirect=${encodeURIComponent(returnUrl)}`);
                                }}
                                className="text-[#077f5d] underline hover:text-[#3cbe8c] font-mono"
                            >
                                {t('oauth_consent.not_you')}
                            </button>
                        </p>
                    </div>

                    {/* Buttons */}
                    <div className="space-y-3">
                        <button
                            onClick={handleApprove}
                            disabled={processing}
                            className="w-full bg-[#3cbe8c] text-white px-8 py-4 border-2 border-[#077f5d] font-bold uppercase transition-all"
                            style={{
                                fontFamily: "'Press Start 2P', monospace",
                                fontSize: '10px',
                                boxShadow: '6px 6px 0 #077f5d'
                            }}
                            onMouseEnter={(e) => {
                                if (!processing) {
                                    e.currentTarget.style.boxShadow = '4px 4px 0 #077f5d';
                                    e.currentTarget.style.transform = 'translate(2px, 2px)';
                                }
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.boxShadow = '6px 6px 0 #077f5d';
                                e.currentTarget.style.transform = 'translate(0, 0)';
                            }}
                        >
                            {processing ? t('oauth_consent.processing') : t('oauth_consent.allow')}
                        </button>

                        <button
                            onClick={handleDeny}
                            disabled={processing}
                            className="w-full bg-white text-[#077f5d] px-6 py-3 border-2 border-[#077f5d] font-bold uppercase transition-all"
                            style={{
                                fontFamily: "'Press Start 2P', monospace",
                                fontSize: '10px',
                                boxShadow: '4px 4px 0 #077f5d'
                            }}
                            onMouseEnter={(e) => {
                                if (!processing) {
                                    e.currentTarget.style.boxShadow = '2px 2px 0 #077f5d';
                                    e.currentTarget.style.transform = 'translate(2px, 2px)';
                                }
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.boxShadow = '4px 4px 0 #077f5d';
                                e.currentTarget.style.transform = 'translate(0, 0)';
                            }}
                        >
                            {t('oauth_consent.deny')}
                        </button>
                    </div>

                    {/* Security Notice */}
                    <p className="mt-6 text-center text-xs font-mono text-gray-500">
                        {t('oauth_consent.revoke_notice')}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default OAuthConsentPage;
